import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withLocalize } from 'react-localize-redux';
import Dropdown from '../../components/molecules/Dropdown';
import css from './ArtifactSwitcher.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.ARTIFACT_SHELF';

const ArtifactSwitcherItem = ({ artifact, position, total, isOpen, fallbackTitle, translate }) => (
    <span className={classNames(css.row, { [css.rowOpen]: isOpen })}>
        <span className={css.title}>{artifact.title || fallbackTitle}</span>
        <span className={css.time}>{artifact.time}</span>
        <span className={css.counter}>{translate(`${TRANSLATION_PREFIX}.COUNTER`, { position, total })}</span>
    </span>
);

ArtifactSwitcherItem.propTypes = {
    artifact: PropTypes.shape({
        title: PropTypes.string,
        time: PropTypes.string,
    }).isRequired,
    position: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    isOpen: PropTypes.bool.isRequired,
    fallbackTitle: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
};

const ArtifactSwitcher = ({ artifacts, openArtifactId, onShowArtifact, translate }) => {
    const fallbackTitle = translate(`${TRANSLATION_PREFIX}.TITLE_FALLBACK`);
    const ordered = useMemo(() => [...artifacts].reverse(), [artifacts]);

    const items = useMemo(
        () =>
            ordered.map((artifact, index) => ({
                name: artifact.id,
                display_name: (
                    <ArtifactSwitcherItem
                        artifact={artifact}
                        position={index + 1}
                        total={ordered.length}
                        isOpen={artifact.id === openArtifactId}
                        fallbackTitle={fallbackTitle}
                        translate={translate}
                    />
                ),
            })),
        [ordered, openArtifactId, fallbackTitle, translate]
    );

    const open = artifacts.find(artifact => artifact.id === openArtifactId) || artifacts[0];

    // One document costs nothing: no chip, no counter, no menu.
    if (artifacts.length < 2) {
        return <span className={css.plainTitle}>{open?.title || fallbackTitle}</span>;
    }

    return (
        <Dropdown
            key={openArtifactId}
            items={items}
            selected={items.find(item => item.name === open?.id)}
            onSelection={item => onShowArtifact(item.name)}
            placement="bottom-start"
            wrapperClass={css.switcher}
            containerClass={css.trigger}
            selectedContainer={css.triggerButton}
            bodyClassName={css.menuBody}
        />
    );
};

ArtifactSwitcher.propTypes = {
    artifacts: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string,
            time: PropTypes.string,
        })
    ).isRequired,
    openArtifactId: PropTypes.string,
    onShowArtifact: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ArtifactSwitcher);
